import { devWarn, devError, devInfo } from "@/lib/logger";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  marketplaceCategories, marketplaceWorkers, marketplaceJobs, marketplaceCreatePayment,
} from "@/lib/api";

const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

/**
 * Stripe Connect 80/20 marketplace widget.
 * - Anyone can be a client (pays a worker).
 * - To be a worker: complete Stripe Connect onboarding in /settings.
 * - Client selects a worker + category + amount → in-app Stripe Checkout → 80% to worker's
 *   connected bank, 20% platform fee auto-split.
 */
export default function MarketplaceWidget() {
  const [categories, setCategories] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    worker_user_id: "",
    service_category: "Custom Business Services",
    description: "",
    amount_dollars: "",
  });

  const load = useCallback(async () => {
    try {
      const [c, w, j] = await Promise.all([
        marketplaceCategories(), marketplaceWorkers(), marketplaceJobs("all"),
      ]);
      setCategories(c.categories || []);
      setWorkers(w.items || []);
      setJobs(j.items || []);
    } catch (err) {
      devWarn("marketplace load failed:", err?.message || err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const amt = parseFloat(form.amount_dollars);
    if (!form.worker_user_id) return toast.error("Pick a worker");
    if (!amt || amt < 1) return toast.error("Amount must be at least $1");
    if (!form.description || form.description.length < 2) return toast.error("Add a short description");
    setBusy(true);
    try {
      const r = await marketplaceCreatePayment({
        worker_user_id: form.worker_user_id,
        service_category: form.service_category,
        description: form.description,
        amount_cents: Math.round(amt * 100),
        currency: "usd",
      });
      toast.success("Redirecting to Stripe Checkout…");
      window.location.href = r.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create payment");
      setBusy(false);
    }
  };

  const workerFor = (uid) => workers.find((w) => w.user_id === uid);

  return (
    <div data-testid="marketplace-widget" className="border border-zinc-800 bg-zinc-950/60 p-5 col-span-2 md:col-span-3">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#22c55e] font-mono">// marketplace · 80/20 auto-split</div>
          <div className="text-white text-sm mt-1">Pay a contractor. Stripe splits <b className="text-[#22c55e]">80%</b> to their bank, <b className="text-[#60a5fa]">20%</b> platform fee — instant.</div>
        </div>
        <button
          data-testid="marketplace-toggle-form"
          onClick={() => setShowForm((s) => !s)}
          className="text-[10px] tracking-[0.3em] uppercase text-[#22c55e] border border-[#22c55e]/50 hover:bg-[#22c55e]/10 px-3 py-2"
        >
          {showForm ? "Close ×" : "+ New Job Payment"}
        </button>
      </div>

      {showForm && (
        <div className="grid gap-3 mb-6 border border-[#22c55e]/30 bg-black/60 p-4" data-testid="marketplace-form">
          <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-200 font-mono">Worker (must have Stripe Connect)</label>
          <select
            data-testid="marketplace-worker-select"
            value={form.worker_user_id}
            onChange={(e) => setForm({ ...form, worker_user_id: e.target.value })}
            className="bg-black border border-zinc-800 focus:border-[#22c55e] focus:outline-none text-white font-mono text-xs px-3 py-2"
          >
            <option value="">— select connected worker —</option>
            {workers.map((w) => (
              <option key={w.user_id} value={w.user_id}>{w.name || w.email} · {w.email}</option>
            ))}
          </select>
          {workers.length === 0 && (
            <div className="text-[10px] text-zinc-300 font-mono">
              // No workers connected yet. Ask your contractor to open <a className="text-[#22c55e] underline" href="/settings">Settings</a> and click <b>Connect Stripe</b> before you can pay them.
            </div>
          )}

          <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-200 font-mono mt-2">Service category</label>
          <select
            data-testid="marketplace-category-select"
            value={form.service_category}
            onChange={(e) => setForm({ ...form, service_category: e.target.value })}
            className="bg-black border border-zinc-800 focus:border-[#22c55e] focus:outline-none text-white font-mono text-xs px-3 py-2"
          >
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>

          <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-200 font-mono mt-2">Description</label>
          <input
            data-testid="marketplace-description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="e.g. Q1 bookkeeping · Jan-Mar 2026"
            maxLength={500}
            className="bg-black border border-zinc-800 focus:border-[#22c55e] focus:outline-none text-white placeholder-zinc-300 font-mono text-xs px-3 py-2"
          />

          <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-200 font-mono mt-2">Amount (USD)</label>
          <div className="flex items-center gap-2">
            <span className="text-[#22c55e] font-mono">$</span>
            <input
              data-testid="marketplace-amount"
              type="number" min="1" step="0.01"
              value={form.amount_dollars}
              onChange={(e) => setForm({ ...form, amount_dollars: e.target.value })}
              placeholder="1000.00"
              className="flex-1 bg-black border border-zinc-800 focus:border-[#22c55e] focus:outline-none text-white placeholder-zinc-300 font-mono text-xs px-3 py-2"
            />
          </div>

          {parseFloat(form.amount_dollars || 0) > 0 && (
            <div className="text-[10px] font-mono text-zinc-200 border-l-2 border-[#22c55e]/40 pl-3 mt-2">
              Worker receives: <span className="text-[#22c55e]">${(parseFloat(form.amount_dollars) * 0.8).toFixed(2)}</span> ·
              Platform fee: <span className="text-[#60a5fa]">${(parseFloat(form.amount_dollars) * 0.2).toFixed(2)}</span>
            </div>
          )}

          <button
            data-testid="marketplace-submit"
            disabled={busy || !form.worker_user_id}
            onClick={create}
            className="mt-3 bg-[#22c55e] text-black font-bold text-xs tracking-[0.3em] uppercase py-3 hover:bg-[#22c55e]/80 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? "Redirecting…" : "Pay via Stripe →"}
          </button>
        </div>
      )}

      {/* Job history */}
      {jobs.length > 0 && (
        <div className="border-t border-zinc-800 pt-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-200 font-mono mb-2">// recent jobs</div>
          {jobs.slice(0, 8).map((j) => {
            const w = workerFor(j.worker_user_id);
            return (
              <div key={j.id} data-testid={`marketplace-job-${j.id}`} className="grid grid-cols-12 gap-2 items-center py-2 border-b border-zinc-900 text-xs font-mono">
                <div className="col-span-2 text-zinc-200">{(j.paid_at || j.created_at || "").slice(0, 10)}</div>
                <div className="col-span-4 text-white truncate">{j.service_category} — <span className="text-zinc-200">{j.description.slice(0, 40)}</span></div>
                <div className="col-span-3 text-zinc-200 truncate">{w?.email || j.worker_user_id.slice(0, 12)}</div>
                <div className="col-span-2 text-[#22c55e] text-right">{fmt(j.amount_cents)}</div>
                <div className={`col-span-1 text-right text-[10px] tracking-[0.25em] uppercase ${j.status === "paid" ? "text-[#22c55e]" : "text-zinc-200"}`}>
                  {j.status}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
